import { SingleAndMultiSelectFieldOption } from 'module/api/field/types';
import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslate } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { getRandomColor } from 'module/ui/components/styles';
import { InputRef } from 'antd';
import { FC, PropsWithChildren, useCallback, useRef } from 'react';
import styled from 'styled-components';

export const FormItem = styled(Form.Item)`
  margin-bottom: 0;
`;

const WrapperComponent: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  return <div className={className}>{children}</div>;
};

interface Props {
  existingOptions: SingleAndMultiSelectFieldOption[];
  addOption: (option: SingleAndMultiSelectFieldOption) => void;
  addedAsFormItem?: boolean;
  className?: string;
  shouldValidate?: boolean;
  placeholderText?: string;
}
export const AddOptionsInput: FC<Props> = ({
  existingOptions: options,
  addedAsFormItem = false,
  addOption,
  className,
  shouldValidate,
  placeholderText,
}) => {
  const translate = useTranslate();
  const requiredMessage = translate('input.required');
  const addOptionsPlaceholderText = placeholderText || translate('add_choice');
  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);
  const onAddOptionInputKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.stopPropagation();
        event.preventDefault();
        form
          .validateFields()
          .then(({ addOptionInput }) => {
            const existingValues = options.map((x) => x.value);

            addOption({
              value:
                existingValues.length > 0 ? Math.max(...existingValues) + 1 : 0,
              label: addOptionInput,
              color: getRandomColor(addOptionInput),
            });
            form.resetFields();
            queueMacrotask(() => {
              inputRef.current?.input?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
              });
              inputRef.current?.focus();
            });
          })
          .catch(() => {});
      }
    },
    [addOption, form, options],
  );
  const onAddOptionInputBlur = useCallback(() => {
    form.setFields([
      {
        name: 'addOptionInput',
        errors: [],
      },
    ]);
  }, [form]);

  return (
    <Form
      layout="vertical"
      component={addedAsFormItem ? WrapperComponent : undefined}
      onSubmitCapture={(event) => {
        event.preventDefault();
      }}
      form={form}
      initialValues={{
        addOptionInput: '',
      }}
      className={className}
    >
      <FormItem
        name={'addOptionInput'}
        required
        rules={[
          { required: true, message: requiredMessage },
          {
            validator: (_, value) => {
              if (shouldValidate) {
                const formattedValue = (value || '').toLowerCase().trim();

                if (
                  options.find(
                    (option) =>
                      option.label.toLowerCase().trim() === formattedValue,
                  )
                ) {
                  return Promise.reject(
                    new Error(
                      translate('input.value_already_exists', {
                        value,
                      }),
                    ),
                  );
                }
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          placeholder={addOptionsPlaceholderText}
          onKeyDown={onAddOptionInputKeyDown}
          onBlur={onAddOptionInputBlur}
          ref={inputRef}
        />
      </FormItem>
    </Form>
  );
};
